/* eslint-disable react-refresh/only-export-components */
import type { UseMutationResult } from '@tanstack/react-query'
import type { FileStepType, FormComponentInfoType, WizardStepConfigType } from 'app/src/features/Shopping/shared'
import { FormItemEnum } from 'app/src/features/Shopping/shared'
import type { DocumentFileType } from 'app/src/model'
import type { documentAiApi, refundApi } from 'app/src/services/welfareportal'
import { formatDate, type WebAppContextType } from 'app/src/shared/helpers'
import { cloneDeep } from 'lodash'

export const SubCategoryGAS = '2'
export const SubCategoryLUCE = '1'
export const DocumentScope = 'Refund'
export const InvoiceUploadStepName = 'InvoiceUpload'
export const InvoiceUpdateStepName = 'InvoiceUpdate'
export const ConfirmationStepName = 'ConfirmationStep'
export const VoucherSelectionStepName = 'VoucherSelection'
export const SuccessFinalStepName = 'SuccessFinalStep'
export const AdditionalDocumentUploadStepName = 'AdditionalDocumentsUpload'
export const KindPosteEnergia = 'poste-energia'
export const KindPosteFibra = 'poste-fibra'

const adaptDocumentInfoAi = (documentInfo: documentAiApi.DocumentInfo): refundApi.DocumentInfo => {
  return {
    ...documentInfo,
    status: documentInfo.status.toString(),
    scope: documentInfo.scope.toString(),
    fileUrl: documentInfo.fileUrl,
  }
}

export const mergeRefundInvoiceData = (
  invoiceData: documentAiApi.InvoiceData | undefined,
  invoiceFormData: {
    invoiceId?: string
    invoiceTotalNumber?: number
    invoiceDate?: string
    vendorName?: string
    vendorTaxId?: string
  },
) => ({
  ...invoiceData,
  invoiceId: {
    ...invoiceData?.invoiceId,
    value: invoiceFormData.invoiceId,
  },
  invoiceTotal: {
    ...invoiceData?.invoiceTotal,
    value: invoiceFormData.invoiceTotalNumber,
  },
  invoiceDate: {
    ...invoiceData?.invoiceDate,
    value: invoiceFormData.invoiceDate,
  },
  vendorName: {
    ...invoiceData?.vendorName,
    value: invoiceFormData.vendorName,
  },
  vendorTaxId: {
    ...invoiceData?.vendorTaxId,
    value: invoiceFormData.vendorTaxId,
  },
})
export const mergePosteEnergiaInvoiceData = (
  invoiceData: documentAiApi.InvoiceData | undefined,
  invoiceFormData: {
    invoiceId?: string
    customerId?: string
    invoiceTotalNumber?: number
    invoiceDate?: string
    dueDate?: string
    providerid?: unknown
    domiciliation?: unknown
    pod?: unknown
    pdr?: unknown
  },
  refund: refundApi.Refund,
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const retObj: any = {
    ...invoiceData,
    invoiceId: {
      ...invoiceData?.invoiceId,
      value: invoiceFormData.invoiceId,
    },
    customerId: {
      ...invoiceData?.customerId,
      value: invoiceFormData.customerId,
    },
    invoiceTotal: {
      ...invoiceData?.invoiceTotal,
      value: invoiceFormData.invoiceTotalNumber,
    },
    invoiceDate: {
      ...invoiceData?.invoiceDate,
      value: invoiceFormData.invoiceDate,
    },
    dueDate: {
      ...invoiceData?.dueDate,
      value: invoiceFormData.dueDate,
    },
    providerid: {
      ...invoiceData?.providerid,
      value: invoiceFormData.providerid,
    },
    domiciliation: {
      ...invoiceData?.domiciliation,
      value: invoiceFormData.domiciliation,
    },
    pod: undefined,
    pdr: undefined,
  }

  if (refund.attributes?.subCategoryId === SubCategoryLUCE) {
    retObj.pod = {
      ...invoiceData?.pod,
      value: invoiceFormData.pod,
    }
  }
  if (refund.attributes?.subCategoryId === SubCategoryGAS) {
    retObj.pdr = {
      ...invoiceData?.pdr,
      value: invoiceFormData.pdr,
    }
  }

  return retObj
}

export const mergePosteFibraInvoiceData = (
  invoiceData: documentAiApi.InvoiceData | undefined,
  invoiceFormData: {
    invoiceId?: string
    invoiceTotalNumber?: number
    dueDate?: string
    providerid?: string
    domiciliation?: string
  },
) => ({
  ...invoiceData,
  invoiceId: {
    ...invoiceData?.invoiceId,
    value: invoiceFormData.invoiceId,
  },
  invoiceTotal: {
    ...invoiceData?.invoiceTotal,
    value: invoiceFormData.invoiceTotalNumber,
  },
  dueDate: {
    ...invoiceData?.dueDate,
    value: invoiceFormData.dueDate,
  },
  providerid: {
    ...invoiceData?.providerid,
    value: invoiceFormData.providerid,
  },
  domiciliation: {
    ...invoiceData?.domiciliation,
    value: invoiceFormData.domiciliation,
  },
})

export const refundForwardAction = async (
  context: WebAppContextType,
  confirmRefundMutation: UseMutationResult<
    refundApi.RefundConfirmResponse,
    refundApi.RefundRequestConfirmError,
    refundApi.RefundRequestConfirmVariables,
    unknown
  >,
  wizardSteps: WizardStepConfigType[],
  currentStep: WizardStepConfigType,
  currentStepIndex: number,
  setCurrentStepIndex: (currentStepIndex: number) => void,
  formData: unknown[],
  refund: refundApi.Refund,
  initializeForm: (formComponentInfo: FormComponentInfoType) => unknown,
  setFormErrors: (value: unknown) => void,
  setForwardActionRequested: (value: boolean) => void,
  setFormData: (formData: unknown[]) => void,
  files: FileStepType[],
  setFiles: (files: FileStepType[]) => void,
  getTargetComponentFormFile: (
    _files: FileStepType[] | undefined,
    formComponent: FormComponentInfoType,
  ) => DocumentFileType | undefined,
  additionalRadioKeys: string[],
  setGenericError: (genericError: JSX.Element | string) => void,
  voucherIds?: string[],
) => {
  const currentFormData = currentStep.formComponentInfo
    ? (formData[currentStepIndex] ?? initializeForm(currentStep.formComponentInfo))
    : undefined

  if (currentStep?.formComponentInfo) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(currentStep?.formComponentInfo?.yupSchema as any).validateSync(currentFormData, { abortEarly: false })
      setFormErrors(null)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      const { inner } = err
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const _formErrors: any = {
        _blockingMessages: [],
      }

      if (inner && inner[0]) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        inner.forEach((error: any) => {
          const { path, message } = error
          console.log('path, message', path, message)
          if (!_formErrors[path]) {
            if (typeof message === 'string') _formErrors[path] = message
            else {
              if (typeof message.isBlocking !== 'undefined' && message.isBlocking === true)
                _formErrors._blockingMessages.push(message.body)
              else _formErrors[path] = message.body
            }
          }
        })
      }
      setFormErrors(_formErrors)
      setForwardActionRequested(false)
      return
    }

    if (!formData[currentStepIndex]) {
      const _formData = cloneDeep(formData)
      _formData[currentStepIndex] = currentFormData
      setFormData(_formData)
    }

    if (currentStep.formComponentInfo?.syncDocumentWithForm) {
      try {
        const updatedFile = await currentStep.formComponentInfo.syncDocumentWithForm(
          currentFormData,
          getTargetComponentFormFile(files, currentStep?.formComponentInfo)!,
        )
        const files_ = cloneDeep(files)
        const docId = files_.find((x) => x.stepName)?.files?.findIndex((x) => x.documentId === updatedFile.documentId)
        if (docId && files_?.some((x) => x.stepName)) {
          const defFiles = files_?.find((x) => x.stepName)?.files
          if (defFiles) defFiles[docId] = updatedFile
          setFiles(files_)
        }
      } catch {
        setForwardActionRequested(false)
        return
      }
    }
  }

  if (currentStep.confirmationStep) {
    let refundResponse: refundApi.RefundConfirmResponse | undefined = undefined
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const invoiceFormData: any = formData[wizardSteps.findIndex((x) => x.stepName === InvoiceUpdateStepName)]
      const invoiceFile = files?.find((x) => x.stepName === InvoiceUploadStepName)?.files[0]

      let attributes: refundApi.RefundAttributes = {}
      for (let i = 0; i < additionalRadioKeys?.length; i++) {
        attributes = {
          ...attributes,
          [additionalRadioKeys[i]]: true,
        }
      }

      if (voucherIds && voucherIds?.length > 0) attributes.voucherIds = voucherIds

      const requestBody: refundApi.RefundConfirmRequest = {
        requestedTotal: invoiceFormData.requestedRefundNumber,
        documents:
          files
            ?.find((x) => x.stepName === AdditionalDocumentUploadStepName)
            ?.files?.map((x) => ({
              documentInfo: adaptDocumentInfoAi(x!.documentDetails!.documentInfo),
              documentInfoSignature: x.documentDetails?.documentInfoSignature ?? '',
              metadata: {},
            })) ?? [],
        invoice: {
          metadata: {},
          dataSignature: invoiceFile?.documentDetails?.dataSignature ?? '',
          documentInfo: adaptDocumentInfoAi(invoiceFile!.documentDetails!.documentInfo!),
          documentInfoSignature: invoiceFile?.documentDetails?.documentInfoSignature ?? '',
          data:
            refund.kind === KindPosteEnergia
              ? mergePosteEnergiaInvoiceData(invoiceFile?.documentDetails?.data, invoiceFormData, refund)
              : refund.kind === KindPosteFibra
                ? mergePosteFibraInvoiceData(invoiceFile?.documentDetails?.data, invoiceFormData)
                : mergeRefundInvoiceData(invoiceFile?.documentDetails?.data, invoiceFormData),
        },
        attributes,
      }

      if (refund.rule?.externalKeys && refund.rule.externalKeys.some((key) => RefundExternalKeysFilter(key, refund))) {
        refund.rule.externalKeys
          .filter((key) => RefundExternalKeysFilter(key, refund))
          .forEach((externalKey) => {
            const definedExternalKey = externalKey.key ?? ''
            if (invoiceFile?.documentDetails?.data?.[definedExternalKey])
              requestBody.invoice!.data[definedExternalKey] = {
                ...invoiceFile?.documentDetails?.data?.[definedExternalKey],
              }
            if (!requestBody.invoice!.data[definedExternalKey]) requestBody.invoice!.data[definedExternalKey] = {}

            requestBody.invoice!.data[definedExternalKey].value =
              externalKey.format === 'number'
                ? invoiceFormData[definedExternalKey + 'Number']
                : invoiceFormData[definedExternalKey]
          })
      }

      refundResponse = await confirmRefundMutation.mutateAsync({ pathParams: { refundId: refund.refundId }, body: requestBody })

      switch (refund.kind) {
        case KindPosteEnergia:
          if (
            !refundResponse?.status ||
            (refundResponse.status.value !== 'SUBMITTED' && refundResponse.status.value !== 'REFUNDED')
          ) {
            throw new Error()
          }
          break
        default:
          if (!refundResponse?.status || refundResponse.status.value !== 'SUBMITTED') {
            throw new Error()
          }
          break
      }
    } catch (err: unknown) {
      context.logger?.error('Refund Confirm Error', {
        refund: refund.refundId,
        refundKind: refund.kind,
        message: refundResponse?.status?.message,
        value: refundResponse?.status?.value,
        error: err,
      })

      setGenericError(
        <div>
          {refundResponse?.status?.message && (
            <p className='bg-[#EAEAEA] p-[10px] text-left text-2xl'>{refundResponse.status.message}</p>
          )}
        </div>,
      )
      setForwardActionRequested(false)
      return
    }
  }
  if (currentStepIndex < wizardSteps.length - 1) setCurrentStepIndex(currentStepIndex + 1)
  setForwardActionRequested(false)
}

export const updateFiles = (
  files: FileStepType[],
  setFiles: (files: FileStepType[]) => void,
  stepName: string,
  stepFiles: DocumentFileType[],
) => {
  const filesList = cloneDeep(files)
  if (filesList && filesList.some((x) => x.stepName === stepName))
    filesList.find((x) => x.stepName === stepName)!.files = stepFiles
  setFiles(filesList)
}

export const onFileLoaded = (
  wizardSteps: WizardStepConfigType[],
  currentStep: WizardStepConfigType,
  files: FileStepType[],
  setFiles: (files: FileStepType[]) => void,
  file: DocumentFileType,
  formData: unknown[],
  setFormData: (formData: unknown[]) => void,
) => {
  const _files: DocumentFileType[] = []
  const stepFiles = files?.find((x) => x.stepName === currentStep?.stepName)?.files ?? []
  if (stepFiles?.length > 0) {
    _files.push(...stepFiles)
  }
  _files.push(file)
  updateFiles(files, setFiles, currentStep.stepName, _files)
  //azzero tutte le dipendenze
  const formData_ = cloneDeep(formData) ?? []
  wizardSteps
    ?.map((x, xindex) => (x.formComponentInfo && x.formComponentInfo.fileStepName === currentStep.stepName ? xindex : -1))
    ?.filter((x) => x >= 0)
    ?.forEach((val) => {
      formData_[val] = undefined
    })
  setFormData(formData_)
}

export const setRefundFormData = (
  currentStep: WizardStepConfigType,
  currentStepIndex: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formData: any[],
  initializeForm: (formComponentInfo: FormComponentInfoType) => unknown,
  setFormData: (formData: unknown[]) => void,
  fieldId: string,
  fieldValue: unknown,
  type: FormItemEnum,
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formData_: any = cloneDeep(formData)
  if (!formData_[currentStepIndex] && currentStep.formComponentInfo)
    formData_[currentStepIndex] = initializeForm(currentStep.formComponentInfo)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (formData_[currentStepIndex]) (formData_[currentStepIndex] as any)[fieldId] = fieldValue
  switch (type) {
    case FormItemEnum.STRING:
      break
    case FormItemEnum.NUMBER:
      try {
        formData_[currentStepIndex][`${fieldId}Number`] = parseFloat((fieldValue as string)?.replace(',', '.'))
      } catch {
        /* empty */
      }
      break
    case FormItemEnum.DATE:
      try {
        formData_[currentStepIndex][`${fieldId}_Formatted`] = formatDate(fieldValue as string)
      } catch {
        /* empty */
      }
      break
  }

  setFormData(formData_)
}
export const setRefundBulkFormData = (
  currentStep: WizardStepConfigType,
  currentStepIndex: number,
  formData: unknown[],
  initializeForm: (formComponentInfo: FormComponentInfoType) => unknown,
  setFormData: (formData: unknown[]) => void,
  fieldIds: string[],
  fieldValues: unknown[],
  types: FormItemEnum[],
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formData_: any = cloneDeep(formData)
  if (!formData_[currentStepIndex] && currentStep.formComponentInfo)
    formData_[currentStepIndex] = initializeForm(currentStep.formComponentInfo)

  fieldIds.forEach((fieldId, index) => {
    formData_[currentStepIndex][fieldId] = fieldValues[index]
    switch (types[index]) {
      case FormItemEnum.STRING:
        break
      case FormItemEnum.NUMBER:
        try {
          formData_[currentStepIndex][`${fieldId}Number`] = parseFloat((fieldValues[index] as string)?.replace(',', '.'))
        } catch {
          /* empty */
        }
        break
      case FormItemEnum.DATE:
        try {
          formData_[currentStepIndex][`${fieldId}_Formatted`] = formatDate(fieldValues[index] as string)
        } catch {
          /* empty */
        }
        break
    }
  })

  setFormData(formData_)
}

export const RefundExternalKeysFilter = (addKey: refundApi.AdditionalKey, refund: refundApi.Refund) => {
  if (addKey.tag != 'rimborso') return false
  else {
    if (!addKey.filter) return true
    else {
      if (!refund) return false
      else {
        const equalTokenized = addKey.filter.split('=')
        if (equalTokenized.length !== 2) return false

        const rightComparingValue = equalTokenized[1]

        const objectNavigationFields = equalTokenized[0].split('.')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let value: any = refund
        objectNavigationFields.forEach((k) => {
          value = value[k]
        })
        if (typeof value === 'string') value = `'${value}'`

        return value === rightComparingValue
      }
    }
  }
}

export const requestedRefundFormatter = Intl.NumberFormat('it', {
  style: 'decimal',
  maximumFractionDigits: 2,
  minimumFractionDigits: 0,
  useGrouping: false,
})
